import React, { useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Plus, Trash2, Truck } from "lucide-react";
import { toast } from "sonner";

export default function AdminVehicles() {
  const [rows, setRows] = useState([]);
  const [drivers, setDrivers] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ plate: "", model: "", capacity_kg: 100, driver_id: "" });
  const [busy, setBusy] = useState(false);

  const load = () => Promise.all([
    api.get("/vehicles"),
    api.get("/users", { params: { role: "driver" } }),
  ]).then(([v, d]) => { setRows(v.data); setDrivers(d.data); });
  useEffect(() => { load(); }, []);

  const create = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.post("/vehicles", { ...form, capacity_kg: Number(form.capacity_kg), driver_id: form.driver_id || null });
      toast.success("Vehicle added");
      setForm({ plate: "", model: "", capacity_kg: 100, driver_id: "" });
      setOpen(false); load();
    } catch (e) { toast.error(formatApiError(e)); } finally { setBusy(false); }
  };

  const del = async (id) => {
    if (!confirm("Delete vehicle?")) return;
    await api.delete(`/vehicles/${id}`); load();
  };

  return (
    <div className="p-6 lg:p-10 max-w-6xl mx-auto" data-testid="admin-vehicles-page">
      <header className="flex items-end justify-between mb-6">
        <div>
          <h1 className="text-3xl font-display font-bold tracking-tight">Vehicles</h1>
          <p className="text-muted-foreground text-sm mt-1">Fleet ready for dispatch.</p>
        </div>
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild><Button className="rounded-full" data-testid="add-vehicle-btn"><Plus className="h-4 w-4 mr-1.5" /> Add vehicle</Button></DialogTrigger>
          <DialogContent>
            <DialogHeader><DialogTitle>New vehicle</DialogTitle></DialogHeader>
            <form onSubmit={create} className="space-y-3" data-testid="add-vehicle-form">
              <div className="grid grid-cols-2 gap-3">
                <div><Label>Plate</Label><Input required value={form.plate} onChange={(e) => setForm({ ...form, plate: e.target.value })} data-testid="veh-plate" /></div>
                <div><Label>Model</Label><Input required value={form.model} onChange={(e) => setForm({ ...form, model: e.target.value })} data-testid="veh-model" /></div>
              </div>
              <div><Label>Capacity (kg)</Label><Input type="number" min={1} value={form.capacity_kg} onChange={(e) => setForm({ ...form, capacity_kg: e.target.value })} data-testid="veh-capacity" /></div>
              <div>
                <Label>Assigned driver</Label>
                <Select value={form.driver_id || "none"} onValueChange={(v) => setForm({ ...form, driver_id: v === "none" ? "" : v })}>
                  <SelectTrigger data-testid="veh-driver"><SelectValue placeholder="None" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">— None —</SelectItem>
                    {drivers.map((d) => <SelectItem key={d.id} value={d.id}>{d.name}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <Button type="submit" disabled={busy} className="w-full rounded-full" data-testid="veh-submit">{busy ? "Adding…" : "Add vehicle"}</Button>
            </form>
          </DialogContent>
        </Dialog>
      </header>

      <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
        {rows.map((v) => {
          const drv = drivers.find((d) => d.id === v.driver_id);
          return (
            <Card key={v.id} className="p-5 rounded-xl border-border bg-card/80" data-testid={`veh-row-${v.id}`}>
              <div className="flex items-start justify-between">
                <div className="flex items-start gap-3">
                  <div className="h-10 w-10 rounded-lg bg-accent/15 text-accent grid place-items-center"><Truck className="h-5 w-5" /></div>
                  <div>
                    <div className="font-display font-semibold">{v.plate}</div>
                    <div className="text-sm text-muted-foreground">{v.model}</div>
                    <div className="text-xs text-muted-foreground mt-1">{v.capacity_kg} kg cap.</div>
                    <div className="text-xs mt-1">{drv ? <span className="text-primary">{drv.name}</span> : <span className="text-muted-foreground">Unassigned</span>}</div>
                  </div>
                </div>
                <Button variant="ghost" size="icon" onClick={() => del(v.id)} className="text-muted-foreground hover:text-destructive"><Trash2 className="h-4 w-4" /></Button>
              </div>
            </Card>
          );
        })}
        {rows.length === 0 && <div className="text-sm text-muted-foreground col-span-full">No vehicles yet.</div>}
      </div>
    </div>
  );
}
